import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useWaysteClient } from '@alliance-disposal/client';
import { Button, Loading, Menu, MenuItem, Tooltip } from '@wayste/sour-ui';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { HandRaisedIcon, PencilIcon, ScissorsIcon } from '@heroicons/react/24/solid';
import * as turf from '@turf/turf';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import CopyZoneModal from './components/CopyZoneModal';
import NegativeZoneModal from './components/NegativeZoneModal';
import PostalCodesModal from './components/PostalCodesModal';
import RadiusFromAddressModal from './components/RadiusFromAddressModal';
import SearchOSMModal from './components/SearchForTownModal';
import { addFeatureToMap } from './utils/addFeatureToMap';
import { isPolygonClosed } from './utils/isPolygonClosed';
mapboxgl.accessToken = 'pk.eyJ1IjoibHVjc291cmd1bSIsImEiOiJjbGE5dGc1ZHIwMWJkM25ud3Vrbzd1N2lxIn0.jALyAIrgB-YWlPilyAKq8A';
const PricingZoneMap = ({ googleMapsApiKey, serviceAreas, auxillaryZones, zones, disabledFeatures, onSubmit, onError, restrictToCountry, }) => {
    const client = useWaysteClient();
    const mapContainer = useRef(null);
    const map = useRef(null);
    const featuresRef = useRef({});
    const del = useRef(false);
    const editMode = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [zonesToDelete, setZonesToDelete] = useState([]);
    const [deleteMode, setDeleteMode] = useState(false);
    const currentEdit = useRef([]);
    const [editModeState, setEditModeState] = useState(null);
    const [helper, setHelper] = useState(null);
    let sourceIDs = [];
    useEffect(() => {
        if (!mapContainer.current) {
            return;
        }
        // Initialize map
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [-70.9, 42.35],
            zoom: 5,
        });
    }, []);
    useEffect(() => {
        const tempFeatures = [];
        // set the features
        serviceAreas?.forEach((serviceArea) => {
            let geoJSON;
            if ('boundary' in serviceArea) {
                geoJSON = JSON.parse(serviceArea.boundary.geoJSON);
            }
            else {
                geoJSON = JSON.parse(serviceArea.serviceArea.geoJSON);
            }
            geoJSON.features.forEach((feature) => {
                if (feature.geometry.type === 'GeometryCollection') {
                    feature.geometry.geometries.forEach((geometry, index) => {
                        if (geometry.type === 'Polygon') {
                            const polygon = turf.polygon(geometry.coordinates);
                            tempFeatures.push(polygon);
                            featuresRef.current[serviceArea.id + index] = polygon;
                        }
                    });
                }
                else if (feature.geometry.type === 'Polygon') {
                    tempFeatures.push(feature);
                    featuresRef.current[serviceArea.id] = feature;
                }
            });
        });
    }, [map.current, serviceAreas]);
    // add geojson to map
    useEffect(() => {
        if (!map.current)
            return; // wait for map to initialize
        if (!serviceAreas)
            return; // wait for serviceAreas to initialize
        sourceIDs.forEach((sourceID) => {
            if (map.current) {
                map.current.removeLayer('fill' + sourceID);
                map.current.removeLayer('outline' + sourceID);
                map.current.removeSource(sourceID);
            }
        });
        sourceIDs.splice(0, sourceIDs.length);
        map.current.on('load', () => {
            // in theory the source would be the service area id
            Object.keys(featuresRef.current).forEach((key) => {
                const sourceID = key;
                const feature = featuresRef.current[key];
                if (!map.current)
                    return;
                if (map.current.getSource(sourceID))
                    return;
                addFeatureToMap(map.current, sourceID, feature, {
                    click: handleFeatureClick,
                });
                sourceIDs.push(sourceID);
            });
            // ----------------- OUTSIDE EXISTING ZONES -----------------
            if (!map.current)
                return;
            map.current.on('click', handleMapClick);
            map.current.on('mousemove', handleMapMouseMove);
            zoomToFitFeatures();
        });
        return () => {
            map.current?.off('click', handleMapClick);
            map.current?.off('mousemove', handleMapMouseMove);
        };
    }, [featuresRef.current]);
    useEffect(() => {
        if (helper === null) {
            zoomToFitFeatures();
        }
    }, [helper]);
    const zoomToFitFeatures = () => {
        if (!map.current || Object.keys(featuresRef.current).length < 1)
            return;
        const bounds = new mapboxgl.LngLatBounds();
        Object.keys(featuresRef.current).forEach((key) => {
            const feature = featuresRef.current[key];
            if (feature.geometry.type === 'Polygon') {
                feature.geometry.coordinates[0].forEach((coordinate) => {
                    bounds.extend([coordinate[0], coordinate[1]]);
                });
            }
        });
        map.current.fitBounds(bounds, {
            padding: 100,
        });
    };
    const handleFeatureClick = (e) => {
        if (!(editMode.current === null && del.current))
            return;
        if (!e.features?.[0]?.source)
            return;
        map?.current?.removeSource(e.features?.[0].source);
        map?.current?.removeLayer('fill' + e.features?.[0].source);
        map?.current?.removeLayer('outline' + e.features?.[0].source);
        delete featuresRef.current?.[e.features?.[0].source];
        setZonesToDelete([...zonesToDelete, e.features?.[0]]);
    };
    const handleMapClick = (e) => {
        handleNextPoint(e.lngLat, { checkForKinks: true });
    };
    const handleNextPoint = (coordinates, options) => {
        if (!map.current)
            return;
        if (!editMode.current)
            return;
        // if it's the first click, just add the point otherwise check for snapping and add the point
        if (currentEdit.current.length === 0) {
            currentEdit.current.push([coordinates.lng, coordinates.lat]);
        }
        else {
            const closestPoint = currentEdit.current.reduce((prev, curr) => {
                if (!map.current)
                    return prev;
                const prevDistance = map.current.project(prev).dist(map.current.project(coordinates));
                const currDistance = map.current.project(curr).dist(map.current.project(coordinates));
                return prevDistance < currDistance ? prev : curr;
            }, currentEdit.current[0]);
            const distance = map.current.project(closestPoint).dist(map.current.project(coordinates));
            if (distance < 10) {
                coordinates.lng = closestPoint[0];
                coordinates.lat = closestPoint[1];
            }
            currentEdit.current.push([coordinates.lng, coordinates.lat]);
        }
        if (currentEdit.current.length >= 2 && map.current) {
            // if the current edit has more than 2 points, draw a line string
            const lineString = {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: currentEdit.current,
                },
            };
            // check if it has "kinks" and stop the edit if it does
            if (options?.checkForKinks && turf.kinks(turf.lineString(currentEdit.current)).features.length > 0) {
                editOver();
                onError?.('Line cannot cross over themselves. Please try again.');
                return;
            }
            const source = map.current.getSource('currentEdit');
            if (source && source.type === 'geojson') {
                source.setData(lineString);
            }
            else {
                map.current.addSource('currentEdit', {
                    type: 'geojson',
                    data: lineString,
                });
                map.current.addLayer({
                    id: 'currentEdit-lines',
                    type: 'line',
                    source: 'currentEdit',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                    paint: {
                        'line-color': '#000',
                        'line-width': 2,
                        'line-dasharray': [2, 2],
                    },
                });
            }
            // if the last point is the same as the first point, close the polygon
            if (isPolygonClosed(currentEdit.current) || (currentEdit.current.length >= 2 && editMode.current === 'circle')) {
                // if we are in draw mode, add the polygon to the map
                if (editMode.current === 'draw' || editMode.current === 'circle') {
                    let polygon = {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'Polygon',
                            coordinates: [currentEdit.current],
                        },
                    };
                    if (editMode.current === 'circle') {
                        const center = turf.point([currentEdit.current[0][0], currentEdit.current[0][1]]);
                        const distanceFromCenter = turf.distance(center, turf.point([coordinates.lng, coordinates.lat]));
                        polygon = turf.circle(center, distanceFromCenter, {
                            units: 'kilometers',
                        });
                    }
                    // find which service areas the edit polygon intersects with
                    const intersectingServiceAreas = Object.keys(featuresRef.current).filter((key) => {
                        const feature = featuresRef.current[key];
                        if (feature.geometry.type === 'Polygon') {
                            return turf.booleanOverlap(polygon, feature);
                        }
                        return false;
                    });
                    if (intersectingServiceAreas.length === 0) {
                        const sourceID = 'newPolygon' + Math.random();
                        // add the polygon to the map
                        addFeatureToMap(map.current, sourceID, polygon, {
                            click: handleFeatureClick,
                        });
                        featuresRef.current[sourceID] = polygon;
                        sourceIDs.push(sourceID);
                    }
                    // combine the polygons if they are in the same service area
                    if (intersectingServiceAreas.length > 0 && polygon.geometry.type === 'Polygon') {
                        let editPolygon;
                        if (editMode.current === 'circle') {
                            const center = turf.point([currentEdit.current[0][0], currentEdit.current[0][1]]);
                            const distanceFromCenter = turf.distance(center, turf.point([coordinates.lng, coordinates.lat]));
                            editPolygon = turf.circle(center, distanceFromCenter, {
                                units: 'kilometers',
                            });
                        }
                        else {
                            editPolygon = turf.polygon([currentEdit.current]);
                        }
                        // combine the polygons
                        const combinedPolygon = intersectingServiceAreas
                            .map((key) => {
                            return featuresRef.current[key];
                        })
                            .reduce((prev, curr) => {
                            if (curr.geometry.type === 'Polygon' && prev.geometry.type === 'Polygon') {
                                const tempPrev = turf.polygon(prev.geometry.coordinates);
                                const tempCurr = turf.polygon(curr.geometry.coordinates);
                                const result = turf.union(tempPrev, tempCurr);
                                if (result) {
                                    return result;
                                }
                                return prev;
                            }
                            return prev;
                        }, editPolygon);
                        if (combinedPolygon) {
                            const sourceID = intersectingServiceAreas[0];
                            // add the polygon to the map
                            // remove the old polygons
                            intersectingServiceAreas
                                .filter((key) => key !== sourceID)
                                .forEach((key) => {
                                delete featuresRef.current[key];
                                if (!map.current)
                                    return;
                                removeFeature(map.current, key);
                            });
                            // set the new polygon
                            featuresRef.current[sourceID] = combinedPolygon;
                            const source = map.current.getSource(sourceID);
                            if (source && source.type === 'geojson') {
                                source.setData(featuresRef.current[sourceID]);
                            }
                            // remove source ids that are in intersecting service areas but not the first one
                            sourceIDs = sourceIDs.filter((id) => {
                                return !intersectingServiceAreas.includes(id);
                            });
                            // add the new source id
                            sourceIDs.push(sourceID);
                        }
                    }
                }
                if (editMode.current === 'cut') {
                    // there are 2 cases here
                    // 1. the edit polygon completely contained within a service area (polygon) - cut a hole in the service area
                    // 2. the edit polygon partially overlaps a service area (polygon) - subtract the edit polygon from the service area
                    // 3. the edit polygon completely overlaps a service area (polygon) - remove the service area
                    // create a turf polygon from the edit polygon
                    const editPolygon = turf.polygon([currentEdit.current]);
                    // check if any of the polygons are completely contained within the edit polygon, if so we return its id
                    const featuresContainedByEditID = Object.keys(featuresRef.current).filter((key) => {
                        const feature = featuresRef.current[key];
                        if (feature.geometry.type === 'Polygon') {
                            return turf.booleanContains(editPolygon, feature);
                        }
                        return false;
                    });
                    for (const featureContainedByEditID of featuresContainedByEditID) {
                        // delete the feature
                        const source = map.current.getSource(featureContainedByEditID);
                        if (source && source.type === 'geojson') {
                            removeFeature(map.current, featureContainedByEditID);
                        }
                        delete featuresRef.current[featureContainedByEditID];
                    }
                    // check if the edit polygon overlaps any of the service areas (polygons)
                    const featuresOverlappedByEditID = Object.keys(featuresRef.current).filter((key) => {
                        const feature = featuresRef.current[key];
                        if (feature.geometry.type === 'Polygon') {
                            return turf.booleanOverlap(editPolygon, feature);
                        }
                        return false;
                    });
                    for (const featureOverlappedByEditID of featuresOverlappedByEditID) {
                        // subtract the edit polygon from the service area
                        const feature = featuresRef.current[featureOverlappedByEditID];
                        if (feature.geometry.type === 'Polygon') {
                            const original = turf.polygon(feature.geometry.coordinates);
                            const combined = turf.difference(original, editPolygon);
                            if (!combined)
                                return;
                            if (combined.geometry.type === 'Polygon') {
                                featuresRef.current[featureOverlappedByEditID] = combined;
                            }
                            else {
                                combined.geometry.coordinates.forEach((x, index) => {
                                    const id = (featureOverlappedByEditID || '') + index + `${Math.random()}`;
                                    featuresRef.current[id] = {
                                        ...combined,
                                        geometry: { coordinates: x, type: 'Polygon' },
                                    };
                                    if (!map.current)
                                        return;
                                    addFeatureToMap(map.current, id, featuresRef.current[id]);
                                    sourceIDs.push(id);
                                });
                                removeFeature(map.current, featureOverlappedByEditID);
                                delete featuresRef.current[featureOverlappedByEditID];
                            }
                            const source = map.current.getSource(featureOverlappedByEditID);
                            if (source && source.type === 'geojson') {
                                source.setData(featuresRef.current[featureOverlappedByEditID]);
                            }
                        }
                    }
                    // check if any of the service area polygons are completely contain the edit polygon
                    const featuresContainsEditID = Object.keys(featuresRef.current).filter((key) => {
                        const feature = featuresRef.current[key];
                        if (feature.geometry.type === 'Polygon') {
                            return turf.booleanContains(feature, editPolygon);
                        }
                        return false;
                    });
                    for (const featureContainsEditID of featuresContainsEditID) {
                        // subtract the edit polygon from the service area
                        const feature = featuresRef.current[featureContainsEditID];
                        if (feature.geometry.type === 'Polygon') {
                            const original = turf.polygon(feature.geometry.coordinates);
                            const combined = turf.difference(original, editPolygon);
                            if (!combined)
                                return;
                            if (combined.geometry.type === 'Polygon') {
                                featuresRef.current[featureContainsEditID] = combined;
                            }
                            else {
                                combined.geometry.coordinates.forEach((x, index) => {
                                    const id = (featuresOverlappedByEditID.join('-') || '') + index + `${Math.random()}`;
                                    featuresRef.current[id] = {
                                        ...combined,
                                        geometry: { coordinates: x, type: 'Polygon' },
                                    };
                                    if (!map.current)
                                        return;
                                    addFeatureToMap(map.current, id, featuresRef.current[id]);
                                    sourceIDs.push(id);
                                });
                                removeFeature(map.current, featureContainsEditID);
                                delete featuresRef.current[featureContainsEditID];
                            }
                            const source = map.current.getSource(featureContainsEditID);
                            if (source && source.type === 'geojson') {
                                source.setData(featuresRef.current[featureContainsEditID]);
                            }
                        }
                    }
                }
                editOver();
            }
        }
    };
    const handleMapMouseMove = (e) => {
        if (!map.current)
            return;
        if (!editMode.current) {
            map.current.getCanvas().style.cursor = '';
            return;
        }
        map.current.getCanvas().style.cursor = 'crosshair';
        // if the length of the current edit is greater than 0, draw a line from the last point to the mouse
        if (!(currentEdit.current.length > 0)) {
            return;
        }
        const coordinates = e.lngLat;
        if (!coordinates)
            return;
        const closestPoint = currentEdit.current.reduce((prev, curr) => {
            if (!map.current)
                return prev;
            const prevDistance = map.current.project(prev).dist(map.current.project(coordinates));
            const currDistance = map.current.project(curr).dist(map.current.project(coordinates));
            return prevDistance < currDistance ? prev : curr;
        }, currentEdit.current[0]);
        const distance = map.current.project(closestPoint).dist(map.current.project(coordinates));
        if (distance < 10) {
            coordinates.lng = closestPoint[0];
            coordinates.lat = closestPoint[1];
        }
        let currentEditFeature = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: [...currentEdit.current, [e.lngLat.lng, e.lngLat.lat]],
            },
        };
        if (editMode.current === 'circle') {
            const center = turf.point([currentEdit.current[0][0], currentEdit.current[0][1]]);
            const distanceFromCenter = turf.distance(center, turf.point([e.lngLat.lng, e.lngLat.lat]));
            currentEditFeature = turf.circle(center, distanceFromCenter, {
                units: 'kilometers',
            });
        }
        const source = map.current.getSource('currentEdit');
        if (source && source.type === 'geojson') {
            source.setData(currentEditFeature);
        }
        else {
            map.current.addSource('currentEdit', {
                type: 'geojson',
                data: currentEditFeature,
            });
            map.current.addLayer({
                id: 'currentEdit-lines',
                type: 'line',
                source: 'currentEdit',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': '#000',
                    'line-width': 2,
                    'line-dasharray': [2, 2],
                },
            });
            // add a circle to the last point
            map.current.addLayer({
                id: 'currentEdit-points',
                type: 'circle',
                source: 'currentEdit',
                paint: {
                    'circle-radius': 5,
                    'circle-color': '#fff',
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#000',
                },
            });
        }
    };
    const removeFeature = (map, id) => {
        if (!map.getSource(id)) {
            return;
        }
        map.removeLayer('fill' + id);
        map.removeLayer('outline' + id);
        map.removeSource(id);
        sourceIDs = sourceIDs.filter((sourceID) => sourceID !== id);
    };
    const editOver = () => {
        if (!map.current) {
            return;
        }
        const source = map.current.getSource('currentEdit');
        if (source && source.type === 'geojson') {
            if (map.current.getLayer('currentEdit-lines')) {
                map.current.removeLayer('currentEdit-lines');
            }
            if (map.current.getLayer('currentEdit-points')) {
                map.current.removeLayer('currentEdit-points');
            }
            map.current.removeSource('currentEdit');
        }
        currentEdit.current = [];
        editMode.current = null;
        setEditModeState(null);
    };
    const simplifyZones = () => {
        if (!map.current) {
            return;
        }
        // see if each polygon is within or intersects with another polygon, if so, combine them
        const polygons = Object.keys(featuresRef.current).map((key) => {
            return featuresRef.current[key];
        });
        // make sure all polygons are valid or remove them by ensure they are 4 points or more
        polygons.forEach((polygon, index) => {
            if (polygon.type !== 'Feature' || polygon.geometry.type !== 'Polygon') {
                removeFeature(map.current, Object.keys(featuresRef.current)[index]);
                delete featuresRef.current[Object.keys(featuresRef.current)[index]];
                return;
            }
            if (polygon.geometry.coordinates.every((x) => x.length < 4)) {
                removeFeature(map.current, Object.keys(featuresRef.current)[index]);
                delete featuresRef.current[Object.keys(featuresRef.current)[index]];
            }
            polygon.geometry.coordinates = polygon.geometry.coordinates.filter((x) => x.length > 4);
            // filter out any set of coordinates that kink
            polygon.geometry.coordinates = polygon.geometry.coordinates.filter((x) => turf.kinks(turf.lineString(x)).features.length === 0);
            //filter out anything with an area less then 1km squared
            polygon.geometry.coordinates = polygon.geometry.coordinates.filter((x) => turf.area(turf.polygon([x])) > 1e6);
            // check the first and last point to see if they are the same
            // filter out any set of coordinates that kink
            const firstCoordinate = polygon.geometry.coordinates[0][0];
            const lastCoordinate = polygon.geometry.coordinates[0][polygon.geometry.coordinates[0].length - 1];
            if (!turf.booleanEqual(turf.point(firstCoordinate), turf.point(lastCoordinate))) {
                polygon.geometry.coordinates[0].push(firstCoordinate);
            }
        });
        const POLY = polygons.map((polygon, index) => {
            const intersectingPolygons = polygons.filter((otherPolygon, otherIndex) => {
                if (index === otherIndex) {
                    return false;
                }
                // check they are both polygons
                if (otherPolygon.geometry.type !== 'Polygon' || polygon.geometry.type !== 'Polygon') {
                    return false;
                }
                polygon.geometry.coordinates = polygon.geometry.coordinates.filter((x) => x.length > 3);
                otherPolygon.geometry.coordinates = otherPolygon.geometry.coordinates.filter((x) => x.length > 3);
                try {
                    return (turf.booleanOverlap(polygon, otherPolygon) ||
                        turf.booleanContains(polygon, otherPolygon) ||
                        turf.booleanContains(otherPolygon, polygon));
                }
                catch (e) {
                    console.error('Error checking for overlap or containment between polygons', e, polygon, otherPolygon);
                    return false;
                }
            });
            if (intersectingPolygons.length > 0) {
                const combinedPolygon = intersectingPolygons.reduce((prev, curr) => {
                    if (curr.geometry.type === 'Polygon' && prev.geometry.type === 'Polygon') {
                        try {
                            const tempPrev = turf.polygon(prev.geometry.coordinates);
                            const tempCurr = turf.polygon(curr.geometry.coordinates);
                            const result = turf.union(tempPrev, tempCurr);
                            if (result && result.geometry.type === 'Polygon') {
                                // filter the result for degenerate polygons
                                result.geometry.coordinates = result?.geometry.coordinates.filter((x) => x.length > 3);
                                removeFeature(map.current, Object.keys(featuresRef.current)[index]);
                                delete featuresRef.current[Object.keys(featuresRef.current)[index]];
                                return result;
                            }
                            return prev;
                        }
                        catch (e) {
                            console.error('Error checking for overlap or containment between polygons', e, polygon, prev);
                            return prev;
                        }
                    }
                    return prev;
                }, polygon);
                return combinedPolygon;
            }
            return polygon;
        });
        // remove the old polygons
        Object.keys(featuresRef.current).forEach((key) => {
            const source = map.current.getSource(key);
            if (source && source.type === 'geojson') {
                removeFeature(map.current, key);
            }
            delete featuresRef.current[key];
        });
        featuresRef.current = {};
        // add the new polygons
        POLY.forEach((polygon) => {
            const sourceID = 'newPolygon' + Math.random();
            addFeatureToMap(map.current, sourceID, polygon, {
                click: handleFeatureClick,
            });
            featuresRef.current[sourceID] = polygon;
            sourceIDs.push(sourceID);
        });
        editOver();
    };
    const handleHelperSelect = (helper) => setHelper(helper);
    const handleRadiusFromAddressSubmit = (res) => {
        // set the edit mode to circle
        editMode.current = 'circle';
        // add the first point to the current edit
        currentEdit.current = [[res.coordinates.lng, res.coordinates.lat]];
        // find any point that would be on the circle and call handle click
        const point = turf.point([res.coordinates.lng, res.coordinates.lat]);
        const circle = turf.circle(point, res.radius, {
            units: res.unit,
        });
        const circlePoints = turf.explode(circle);
        // grab the first point
        const firstPoint = circlePoints.features[0];
        // call handle click
        handleNextPoint({
            lng: firstPoint.geometry.coordinates[0],
            lat: firstPoint.geometry.coordinates[1],
        });
        setHelper(null);
    };
    const handleSubmit = () => {
        const features = Object.values(featuresRef.current).map((feature) => {
            const serviceArea = {
                serviceArea: JSON.stringify(feature),
                serviceAreaFormat: 'GEOJSON',
            };
            // if not, it's a new feature
            return serviceArea;
        });
        onSubmit(features);
    };
    const handleSearchOSM = async (searchString) => {
        const res = await axios.get(`https://nominatim.openstreetmap.org/search`, {
            params: {
                format: 'json',
                q: searchString,
                limit: 5,
                countrycodes: restrictToCountry || 'US',
                polygon_geojson: 1,
                addressdetails: 1,
            },
        });
        return res.data;
    };
    const handleAddZipcode = async (postalCodes, mode) => {
        // verify that the zipcode is valid
        const validZipcodes = postalCodes.filter((zipcode) => zipcode.match(/^\d{5}$/));
        setIsLoading(true);
        const featureCollection = await client.pricing().zip.query(validZipcodes.join(','));
        setIsLoading(false);
        featureCollection.forEach((feature) => {
            if (!feature.geoJSON) {
                console.warn('No GeoJSON found for zipcode', feature);
                return;
            }
            const geoJSON = JSON.parse(feature.geoJSON);
            geoJSON.features.forEach((feature) => {
                if (feature.geometry.type === 'Polygon') {
                    feature.geometry.coordinates.forEach((coordinateSet, index) => {
                        editMode.current = index === 0 && mode === 'draw' ? 'draw' : 'cut';
                        currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                        handleNextPoint({
                            lng: coordinateSet[0][0],
                            lat: coordinateSet[0][1],
                        });
                    });
                }
                else if (feature.geometry.type === 'GeometryCollection') {
                    feature.geometry.geometries.forEach((geometry) => {
                        if (geometry.type === 'Polygon') {
                            geometry.coordinates.forEach((coordinateSet, index) => {
                                editMode.current = index === 0 && mode === 'draw' ? 'draw' : 'cut';
                                currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                                handleNextPoint({
                                    lng: coordinateSet[0][0],
                                    lat: coordinateSet[0][1],
                                });
                            });
                        }
                    });
                }
            });
        });
    };
    const handleSubtractZone = (zone) => {
        if (!zone) {
            return;
        }
        const featureCollection = zone.serviceAreas.map((serviceArea) => {
            let geoJSON;
            let kmlFragment;
            if ('boundary' in serviceArea) {
                geoJSON = serviceArea.boundary.geoJSON;
                kmlFragment = serviceArea.boundary.kml;
            }
            else {
                geoJSON = serviceArea.serviceArea.geoJSON;
                kmlFragment = serviceArea.serviceArea.kml;
            }
            return {
                geoJSON: geoJSON,
                kmlFragment: kmlFragment,
            };
        });
        featureCollection.forEach((feature) => {
            const geoJSON = JSON.parse(feature.geoJSON);
            geoJSON.features.forEach((feature) => {
                if (feature.geometry.type === 'Polygon') {
                    feature.geometry.coordinates.forEach((coordinateSet) => {
                        editMode.current = 'cut';
                        currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                        handleNextPoint({
                            lng: coordinateSet[0][0],
                            lat: coordinateSet[0][1],
                        });
                    });
                }
                else if (feature.geometry.type === 'GeometryCollection') {
                    feature.geometry.geometries.forEach((geometry) => {
                        if (geometry.type === 'Polygon') {
                            geometry.coordinates.forEach((coordinateSet) => {
                                editMode.current = 'cut';
                                currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                                handleNextPoint({
                                    lng: coordinateSet[0][0],
                                    lat: coordinateSet[0][1],
                                });
                            });
                        }
                    });
                }
            });
        });
    };
    const handleCopyZone = (zone) => {
        if (!zone) {
            return;
        }
        const featureCollection = zone.serviceAreas.map((serviceArea) => {
            return {
                geoJSON: serviceArea.boundary.geoJSON,
                kmlFragment: serviceArea.boundary.kml,
            };
        });
        featureCollection.forEach((feature) => {
            const geoJSON = JSON.parse(feature.geoJSON);
            geoJSON.features.forEach((feature) => {
                if (feature.geometry.type === 'Polygon') {
                    feature.geometry.coordinates.forEach((coordinateSet) => {
                        editMode.current = 'draw';
                        currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                        handleNextPoint({
                            lng: coordinateSet[0][0],
                            lat: coordinateSet[0][1],
                        });
                    });
                }
                else if (feature.geometry.type === 'GeometryCollection') {
                    feature.geometry.geometries.forEach((geometry) => {
                        if (geometry.type === 'Polygon') {
                            geometry.coordinates.forEach((coordinateSet) => {
                                editMode.current = 'draw';
                                currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                                handleNextPoint({
                                    lng: coordinateSet[0][0],
                                    lat: coordinateSet[0][1],
                                });
                            });
                        }
                    });
                }
            });
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-2", children: [!disabledFeatures?.includes('RADIUS_FROM_ADDRESS') && (_jsx(RadiusFromAddressModal, { open: helper === 'RADIUS_FROM_ADDRESS', googleMapsApiKey: googleMapsApiKey, onCancel: () => {
                    setHelper(null);
                }, onSubmit: handleRadiusFromAddressSubmit })), zones && !disabledFeatures?.includes('ZONE_AS_NEGATIVE_SPACE') && (_jsx(NegativeZoneModal, { open: helper === 'ZONE_AS_NEGATIVE_SPACE', onCancel: () => setHelper(null), zones: zones, onSubmit: (zone) => {
                    handleSubtractZone(zone);
                    setHelper(null);
                } })), auxillaryZones && !disabledFeatures?.includes('COPY_ZONE') && (_jsx(CopyZoneModal, { open: helper === 'COPY_ZONE', zones: auxillaryZones, onCancel: () => setHelper(null), onSubmit: (zone) => {
                    handleCopyZone(zone);
                    setHelper(null);
                } })), !disabledFeatures?.includes('ADD_ZIPCODE') && (_jsx(PostalCodesModal, { open: helper === 'ADD_ZIPCODE', onClose: () => setHelper(null), onSubmit: handleAddZipcode })), !disabledFeatures?.includes('ADD_CITY') && (_jsx(SearchOSMModal, { open: helper === 'ADD_CITY', onCancel: () => setHelper(null), onSearch: handleSearchOSM, onSubmit: (res, mode) => {
                    res.coordinates.forEach((coordinateSet) => {
                        editMode.current = mode || 'draw';
                        currentEdit.current = coordinateSet.map((coordinate) => [coordinate[0], coordinate[1]]);
                        handleNextPoint({ lng: coordinateSet[0][0], lat: coordinateSet[0][1] });
                        setHelper(null);
                    });
                    setHelper(null);
                } })), _jsxs("div", { className: "flex flex-row items-center justify-start gap-2 overflow-hidden rounded bg-white p-2 shadow-md", children: [isLoading && _jsx(Loading, {}), _jsx(Tooltip, { text: "Move around the map", children: _jsx(Button, { onClick: () => {
                                editMode.current = null;
                                setEditModeState(null);
                                del.current = false;
                                setDeleteMode(false);
                            }, className: "btn-icon size-9 p-1", type: "button", children: _jsx(HandRaisedIcon, { className: `size-6 ${!editModeState && !deleteMode ? 'text-primary-400' : 'text-black text-opacity-50'}` }) }) }), _jsx("hr", { className: "h-auto flex-shrink-0 self-stretch border-0 border-r border-solid border-gray-200" }), _jsx(Tooltip, { text: "Draw a new area", children: _jsx(Button, { className: "btn-icon size-9 p-1", type: "button", onClick: () => {
                                setEditModeState('draw');
                                editMode.current = 'draw';
                                currentEdit.current = [];
                            }, children: _jsx(PencilIcon, { className: `size-6 ${editModeState === 'draw' ? 'text-primary-400' : 'text-black text-opacity-50'}` }) }) }), _jsx("hr", { className: "h-auto flex-shrink-0 self-stretch border-0 border-r border-solid border-gray-200" }), _jsx(Tooltip, { text: "Draw to remove an area", children: _jsx(Button, { className: "btn-icon size-9 p-1", type: "button", onClick: () => {
                                setEditModeState('cut');
                                editMode.current = 'cut';
                                currentEdit.current = [];
                            }, children: _jsx(ScissorsIcon, { className: `size-6 ${editModeState === 'cut' ? 'text-primary-400' : 'text-black text-opacity-50'}` }) }) }), _jsx("hr", { className: "h-auto flex-shrink-0 self-stretch border-0 border-r border-solid border-gray-200" }), _jsx(Tooltip, { text: "Draw a circle", children: _jsx(Button, { className: "btn-icon size-9 p-1", type: "button", onClick: () => {
                                setEditModeState('circle');
                                editMode.current = 'circle';
                                currentEdit.current = [];
                            }, children: _jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", className: editModeState === 'circle' ? 'fill-primary-400' : 'fill-black opacity-50', children: _jsx("path", { d: "M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3-8c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z" }) }) }) }), _jsxs(Menu, { title: "Helpers", button: _jsxs("div", { "aria-haspopup": "true", className: "btn-primary-text-only", onClick: () => {
                                editOver();
                            }, children: ["Helpers", _jsx(ChevronDownIcon, { className: "ml-1 size-5" })] }), children: [!disabledFeatures?.includes('COPY_ZONE') && auxillaryZones ? (_jsx(MenuItem, { onClick: () => {
                                    handleHelperSelect('COPY_ZONE');
                                }, children: "Copy From Existing Zones" })) : (_jsx(_Fragment, {})), !disabledFeatures?.includes('RADIUS_FROM_ADDRESS') ? (_jsx(MenuItem, { onClick: () => {
                                    handleHelperSelect('RADIUS_FROM_ADDRESS');
                                }, children: "Radius from Address" })) : (_jsx(_Fragment, {})), !disabledFeatures?.includes('ZONE_AS_NEGATIVE_SPACE') ? (_jsx(MenuItem, { onClick: () => {
                                    handleHelperSelect('ZONE_AS_NEGATIVE_SPACE');
                                }, disabled: !zones, children: "Use Zone as Negative" })) : (_jsx(_Fragment, {})), !disabledFeatures?.includes('ADD_ZIPCODE') ? (_jsx(MenuItem, { onClick: () => {
                                    handleHelperSelect('ADD_ZIPCODE');
                                }, children: "Add/Cut Zip Codes" })) : (_jsx(_Fragment, {})), !disabledFeatures?.includes('ADD_CITY') ? (_jsx(MenuItem, { onClick: () => {
                                    handleHelperSelect('ADD_CITY');
                                }, children: "Add/Cut City or County" })) : (_jsx(_Fragment, {})), !disabledFeatures?.includes('SIMPLIFY_ZONES') ? (_jsx(MenuItem, { onClick: () => {
                                    simplifyZones();
                                }, children: "Simplify Zone" })) : (_jsx(_Fragment, {}))] }), _jsx(Button, { className: "btn-icon ml-auto size-9 p-1", type: "button", onClick: handleSubmit, children: _jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", className: "fill-black opacity-50", children: _jsx("path", { d: "M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" }) }) })] }), _jsx("div", { className: "relative overflow-hidden rounded bg-white shadow-md", children: _jsx("div", { className: "h-[75vh] w-full", ref: mapContainer }) })] }));
};
export default PricingZoneMap;
//# sourceMappingURL=MapEditor.js.map